import BulkProjectActions from "@app/home/bulk-project-actions/BulkProjectActions";
import { ResourceType } from "@app/resources";
import ResourceUpdateModel from "@app/resources/resourceUpdateModel";
import { ColumnConfig, WafflesFilterType } from "@app/shared/waffles";
import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { toast } from "react-toastify";
import { FlyoutLink } from "@app/shared/Flyouts";
import moment from "moment";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { Message, MessageType } from "@app/notifications";
import { MemberSetActivationJobStatusChangeNotificationMessagePayload, } from "@app/notifications/message";
import { JobStatus } from "@app/jobs";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { FilterCriteriaModel, FilterCriteria, FilterCriteriaOnChangeResponse } from "@app/shared/FilterCriteria";
import { formatDate } from "@app/shared/formatDateTime";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import Project from "@app/projects/project";
import ProjectService from "@app/projects/projectService";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import ResourceService from "@app/resources/resourceService";
import { MemberSetVersionStatus, MemberSetTypeEnum } from "@app/members/enumerations";
import MemberSetArchiveFlyout from "@app/members/MemberSetArchiveFlyout";
import MemberSetCountByCountyTable from "@app/members/MemberSetCountByCountyTable";
import MemberSetCountByStateTable from "@app/members/MemberSetCountByStateTable";
import MemberSetEditResourceFlyout from "@app/members/MemberSetEditResourceFlyout";
import { MemberSetModel, Version } from "@app/members/memberSetModels";
import { MemberSetOverallSummary } from "@app/members/MemberSetOverallSummary";
import MemberSetPreviewTable from "@app/members/MemberSetPreviewTable";
import MemberSetService from "@app/members/memberSetService";
import { Unauthorized } from "@app/shared/ErrorPages";
import { NotificationContext } from "@app/notifications/NotificationContext";

type ProjectModule = "Summary Information" | "Preview";

interface MemberSetRouteProps {
  memberSetId: string;
}

const MemberSetDetail: FC = () => {
  const memberSetService = useMemo(() => new MemberSetService(), []);
  const resourceService = useMemo(() => new ResourceService(), []);
  const projectService = useMemo(() => new ProjectService(), []);
  const user = useContext(UserContext);
  const notificationContext = useContext(NotificationContext);
  const routeMatch = useRouteMatch<MemberSetRouteProps>();
  const userIsQuestAdmin = user.roles.includes(Roles.QUEST_ADMIN);
  const userIsClientAdmin = user.roles.includes(Roles.CLIENT_ADMIN);
  const userIsResourceAdmin = user.roles.includes(Roles.RESOURCE_ADMIN);

  const [activateVersionButtonDisabled, setActivateVersionButtonDisabled] = useState(true);
  const [activeModule, setActiveModule] = useState<ProjectModule>("Summary Information");
  const [associatedProjects, setAssociatedProjects] = useState<Project[]>([]);
  const [availableFilteredMemberSets, setAvailableFilteredMemberSets] = useState<MemberSetModel[]>([]);
  const [compareMode, setCompareMode] = useState(false);
  const [error, setError] = useState("");
  const [hasFilterChanged, setHasFilterChanged] = useState(false);
  const [initialFiltersForSelectedVersion, setInitialFiltersForSelectedVersion] = useState<FilterCriteriaModel[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingResourceIds, setLoadingResourceIds] = useState(true);
  const [memberCount, setMemberCount] = useState(0);
  const [memberSet, setMemberSet] = useState<MemberSetModel>(null);
  const [processingFilters, setProcessingFilters] = useState(false);
  const [selectedCompareVersion, setSelectedCompareVersion] = useState<Version>(null);
  const [selectedCompareVersionKey, setSelectedCompareVersionKey] = useState(0);
  const [selectedFilteredMemberSetActivateOptions, setSelectedFilteredMemberSetActivateOptions] = useState<MemberSetModel[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<Version>(null);
  const [selectedVersionKey, setSelectedVersionKey] = useState<number>(null);
  const [showActivationButtons, setShowActivationButtons] = useState(false);
  const [sourceMemberSetInfo, setSourceMemberSetInfo] = useState("");
  const [usedColumns, setUsedColumns] = useState<string[]>([]);
  const [userIsOwner, setUserIsOwner] = useState(false);

  const getMemberSetId = () => {
    return Number(routeMatch.params.memberSetId);
  }

  const memberSetRef = useRef(memberSet);
  const selectedVersionKeyRef = useRef(selectedVersionKey);

  useEffect(() => {
    memberSetRef.current = memberSet;
  }, [memberSet]);

  useEffect(() => {
    selectedVersionKeyRef.current = selectedVersionKey;
  }, [selectedVersionKey]);

  useEffect(() => {
    notificationContext.subscribe(handleActivationJobStatus);
    fetchMemberSet();

    return () => {
      notificationContext.unsubscribe(handleActivationJobStatus);
    }
  }, []);

  const handleVersionChange = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const newVersionKey = Number(event.target.value);
    const newVersion = memberSet.memberSetVersions.filter((option) =>
      option.memberSetVersionId === newVersionKey).pop();
    const versionStatus = newVersion.memberSetVersionStatus;
    const newFilters = await fetchFiltersForSelectedVersion(newVersion.memberSetVersionId);
    const newShowActivationButtons = canBeModified(userIsQuestAdmin, userIsResourceAdmin, memberSet.resource.userId === user.userId, memberSet.resource.isShared, memberSet.resource.isSystemResource) &&
      versionStatus !== MemberSetVersionStatus.Activated;

    setActivateVersionButtonDisabled(canBeActivated(memberSet, newVersion, newFilters));
    setInitialFiltersForSelectedVersion(newFilters);
    setSelectedVersion(newVersion);
    setSelectedVersionKey(newVersionKey);
    setShowActivationButtons(newShowActivationButtons);
  }

  const handleCompareVersionChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const newCompareVersionKey = event.target.value === "" ? 0 : Number(event.target.value);
    const newCompareVersion = event.target.value === "" ? null : memberSet.memberSetVersions.filter((option) =>
      option.memberSetVersionId === newCompareVersionKey).pop();

    const newCompareMode = event.target.value === "" ? false : true;

    setActiveModule("Summary Information");
    setCompareMode(newCompareMode);
    setSelectedCompareVersion(newCompareVersion);
    setSelectedCompareVersionKey(newCompareVersionKey);
  }

  const handleActivateVersionButtonClick = async (): Promise<void> => {
    const requestedVersionId = selectedVersionKey;
    setActivateVersionButtonDisabled(true);
    setIsProcessing(true);

    toast(`Activation processing.`, {
      containerId: "z-index-1030",
      type: "info",
    });

    try {
      await memberSetService.activateMemberSetVersion(
        memberSet.memberSetId,
        requestedVersionId,
        selectedFilteredMemberSetActivateOptions.map(x => x.memberSetId)
      );
    } catch {
      setActivateVersionButtonDisabled(false);
      setError("We are unable to activate this member population at this time. Please try again later.");
      setIsProcessing(false);
      return;
    }

    await refreshAfterActivationRequest(requestedVersionId);
  }

  // A Full member set is activated (or has failed) by the time the request returns, and a Census member set
  // is Processing until its job notifies. Reading the status back means the page no longer depends on the
  // notification reaching this browser to leave the "being activated" state.
  const refreshAfterActivationRequest = async (requestedVersionId: number): Promise<void> => {
    try {
      const memberSet = await memberSetService.getById(getMemberSetId(), false, true);
      await fetchMemberSetResponse(memberSet);

      // The page re-sorts this list in place, so find the requested version by id rather than by position.
      const requestedVersion = memberSet.memberSetVersions?.find((x) => x.memberSetVersionId === requestedVersionId);
      if (requestedVersion?.memberSetVersionStatus === MemberSetVersionStatus.Activated) {
        toastActivated(requestedVersion.memberSetVersionId);
      }
    } catch {
      setError("We could not confirm the result of this activation. Please refresh the page and check the status of this version.");
      setIsProcessing(false);
    }
  }

  const toastActivated = (memberSetVersionId: number): void => {
    // The activation notification can also arrive for the same version, so share an id to show one toast.
    toast(`Your member population was successfully activated.`, {
      containerId: "z-index-1030",
      toastId: `member-set-activated-${memberSetVersionId}`,
      type: "success",
    });
  }

  const handleActivationJobStatus = async (message: Message) => {
    if (!message || message.notificationType !== MessageType.MemberSetActivationJobStatusChange) {
      return;
    }

    const payload = message.payload as MemberSetActivationJobStatusChangeNotificationMessagePayload;
    const currentMemberSet = memberSetRef.current;
    if (!currentMemberSet ||
      (payload.memberSetId !== currentMemberSet.sourceMemberSetId && payload.memberSetId !== currentMemberSet.memberSetId) ||
      payload.memberSetVersionId !== selectedVersionKeyRef.current) {
      return;
    }

    if (payload.jobStatus === JobStatus.Finished) {
      await fetchMemberSet();
      toastActivated(payload.memberSetVersionId);
    }
  }

  const handleArchiveMemberSetSubmit = async (): Promise<void> => {
    memberSetService.archiveMemberSet(memberSet.memberSetId).then(() => {
      fetchMemberSet();
      toast(`Your member population is being archived.`, {
        containerId: "z-index-1030",
        type: "success",
      });
    }, () => {
      setError("We are unable to archive this member population at this time. Please try again later.");
    });
  }

  const handleEditMemberSetSubmit = async (resourceModel: ResourceUpdateModel): Promise<void> => {
    resourceService.updateResource(memberSet.memberSetId, resourceModel).then(() => {
      fetchMemberSet();
      toast(`Your member population was successfully edited.`, {
        containerId: "z-index-1030",
        type: "success",
      });
    }, () => {
      setError("We are unable to process these changes at this time. Please try again later.");
    });
  }

  const getMemberSetArchivePanelContent = (): JSX.Element => {
    return <MemberSetArchiveFlyout onSubmit={handleArchiveMemberSetSubmit} loadingProjects={loadingResourceIds} associatedProjects={associatedProjects} />;
  }

  const getEditMemberSetSidePanelContent = (): JSX.Element => {
    return <MemberSetEditResourceFlyout onSubmit={handleEditMemberSetSubmit} memberSet={memberSet} />;
  }

  const fetchMemberSet = async (): Promise<void> => {
    try {
      const memberSet = await memberSetService.getById(getMemberSetId(), false, true);
      await fetchMemberSetResponse(memberSet);
    } catch {
      setError("unauthorized");
    }
  }

  const fetchSourceMemberSet = async (sourceId: number, sourceVersionId: number): Promise<void> => {
    try {
      const sourceMemberSet = await memberSetService.getById(sourceId, false, true);
      const sourceVersion = sourceMemberSet.memberSetVersions.filter((x) => x.memberSetVersionId === sourceVersionId)[0];
      setSourceMemberSetInfo(sourceMemberSet.resource.name + " - " + formatDate(sourceVersion.dateLastUpdated));
    } catch {
      setSourceMemberSetInfo("");
    }
  }

  const getBulkProjectActionsContent = () => {
    return (
      <BulkProjectActions
        loadingResourceIds={loadingResourceIds}
        resourceIds={associatedProjects.map((project) => {
          return project.projectId;
        })}
        onPublish={() => null}
        onSubmit={handleActivateVersionButtonClick}
        showPublish={false}
        showArchive={false}
        setProjectIsReadOnly={() => null}
      />
    );
  }

  const fetchMemberSetResponse = async (response: MemberSetModel): Promise<void> => {
    const hasVersion = (response.memberSetVersions && response.memberSetVersions.length > 0);
    const memberSetVersion = hasVersion ? response.memberSetVersions[response.memberSetVersions.length - 1] : null;
    const isUserOwner = response.resource.userId === user.userId;
    const newShowActivationButtons = canBeModified(userIsQuestAdmin, userIsResourceAdmin, isUserOwner, response.resource.isShared, response.resource.isSystemResource) &&
      hasVersion && (memberSetVersion.memberSetVersionStatus !== MemberSetVersionStatus.Activated);

    let filteredMemberSets: MemberSetModel[] = [];
    if (newShowActivationButtons && response.resource.resourceType === ResourceType.MemberSet) {
      filteredMemberSets = await memberSetService.getFilteredMemberSets(response.memberSetId);
      filteredMemberSets = filteredMemberSets.filter(x => !x.resource.dateArchived);
    }

    const newIsProcessing = memberSetVersion.memberSetVersionStatus === MemberSetVersionStatus.Processing;
    setActivateVersionButtonDisabled(canBeActivated(response, memberSetVersion, initialFiltersForSelectedVersion, newIsProcessing));
    setAvailableFilteredMemberSets(filteredMemberSets);
    setIsProcessing(newIsProcessing);
    setMemberSet(response);
    setSelectedFilteredMemberSetActivateOptions(filteredMemberSets);
    setSelectedVersion(memberSetVersion);
    setSelectedVersionKey(hasVersion ? memberSetVersion.memberSetVersionId : null);
    setShowActivationButtons(newShowActivationButtons);
    setUserIsOwner(isUserOwner);

    buildFilterCols(response, memberSetVersion);
    getProjectIds(response);

    if (!!response.sourceMemberSetId) {
      const latestVersion = response.memberSetVersions.sort((a, b) => new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1)[0];
      await fetchSourceMemberSet(response.sourceMemberSetId, latestVersion.sourceMemberSetVersionId);
      buildFilterCols(response, latestVersion);
    }

  }

  const updateFilteredMemberSetActivateOptions = (response: MultiselectOnChangeResponse) => {
    const selectedMemberSets = response.checkedKeys;
    const newMemberSets = selectedMemberSets.map((memberSetId) => {
      const matchedRoleModel = availableFilteredMemberSets.find((x) => x.resource.resourceId.toString() === memberSetId);
      return matchedRoleModel;
    });
    setSelectedFilteredMemberSetActivateOptions(newMemberSets);
  }

  const getMemberSetMultiSelectOptions = (memberSets: MemberSetModel[]) => {
    const options: { [key: string]: MultiselectItem } = {};

    if (!!memberSets) {
      memberSets.forEach((memberSet) => Object.assign(options,
        {
          [memberSet.resource.resourceId]: {
            currentState: selectedFilteredMemberSetActivateOptions.some((x) => x.resource.resourceId === memberSet.resource.resourceId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: memberSet.resource.name,
          }
        }));
    }

    return options;
  }

  if ((memberSet && memberSet.resource.isSystemResource && !userIsQuestAdmin && !userIsClientAdmin) || error === "unauthorized") {
    return <Unauthorized />;
  }

  let membersetVersionsDropdown: JSX.Element;
  let membersetCompareVersionsDropdown: JSX.Element;

  if (memberSet && memberSet.memberSetVersions && memberSet.memberSetVersions.length > 0) {
    //render full select box
    membersetVersionsDropdown = (
      <QESSelect
        label={"Version:\xa0"}
        value={selectedVersionKey}
        testId="member-set-detail__member-set-version-list-select"
        onChange={(e) => handleVersionChange(e)}
      >
        {
          memberSet.memberSetVersions.sort((a, b) => a.memberSetVersionId < b.memberSetVersionId ? 1 : -1).map((option) => (
            <option
              key={option.memberSetVersionId}
              value={option.memberSetVersionId}
              disabled={option.dateLastUpdated === selectedVersion.dateLastUpdated}
            >
              {`${moment.utc(option.dateLastUpdated).local().format("MMM DD, YYYY h:mm:ss A")}`}
            </option>
          ))
        }
      </QESSelect>
    );
  } else {
    //render mock select
    membersetVersionsDropdown = <QESSelect label="Version: " testId="member-set-detail__member-set-version-list-select" placeholder="Select a Member Set Version" defaultValue="" />;
  }

  if (memberSet && memberSet.memberSetVersions && memberSet.memberSetVersions.length > 1) {
    membersetCompareVersionsDropdown = (
      <QESSelect
        label={"Compare:\xa0"}
        value={selectedCompareVersionKey}
        testId="member-set-detail__member-set-compare-version-list-select"
        onChange={(e) => handleCompareVersionChange(e)}
        containerClassName="col-6 ps-2"
      >
        {
          [<option value="" key="">None</option>].concat(
            memberSet.memberSetVersions.sort((a, b) => a.memberSetVersionId < b.memberSetVersionId ? 1 : -1).map((option) => (
              option.memberSetVersionId !== selectedVersionKey ?
                (<option key={option.memberSetVersionId} value={option.memberSetVersionId}>{`${moment.utc(option.dateLastUpdated).local().format("MMM DD, YYYY h:mm:ss A")}`}</option>) : null)))
        }
      </QESSelect>
    );
  } else {
    membersetCompareVersionsDropdown = <QESSelect label={"Compare:\xa0"} placeholder="None" testId="member-set-detail__member-set-compare-version-list-select" defaultValue="" containerClassName="col-6 ps-2" />;
  }

  const description = (
    <div className="row ms-3 mt-3">
      <p>
        {(memberSet ? memberSet.resource.description : null)}
      </p>
    </div>
  );

  let activationAlert: JSX.Element = null;
  if (isProcessing) {
    activationAlert = (
      <div className="col-12 ms-3 mt-3">
        <QESAlert key="ActivateAlert" type="info" persist={true}>
          <div className="pull-left">This network version is being activated.</div>
        </QESAlert>
      </div>);
  } else if (activateVersionButtonDisabled) {
    activationAlert = (
      <div className="col-12 ms-3 mt-3">
        <QESAlert key="ActivateAlert" type="warning" persist={true}>
          <div className="mt-2 pull-left"><strong>Warning!</strong> This network version has not been activated.
          </div>
        </QESAlert>
      </div>);
  } else {
    const filteredProvidersMultiSelect = (
      <>
        <QESMultiSelect
          containerClassName="col-8"
          label="These associated filtered networks can also be activated"
          onChange={updateFilteredMemberSetActivateOptions}
          options={getMemberSetMultiSelectOptions(availableFilteredMemberSets)}
          testId="provider-detail__activated-filtered-providers"
        />
        <div className="mb-3">
          <QESButton
            qesTestId="provider-detail__select-all-filtered-providers"
            qesType="secondary"
            onClick={() => setSelectedFilteredMemberSetActivateOptions(availableFilteredMemberSets)}>
            Select All
          </QESButton>
          <QESButton
            qesTestId="provider-detail__clear-all-filtered-providers"
            qesType="secondary"
            onClick={() => setSelectedFilteredMemberSetActivateOptions([])}>
            Clear All
          </QESButton>
        </div>
      </>
    );

    activationAlert = (
      <div key="ActivateAlert" className="col-12 ms-3 mt-3">
        <QESAlert type="warning" persist={true}>
          <div className="mt-1">
            <div className="pull-left"><strong>Warning!</strong> This network version has not been activated.</div>
            <FlyoutLink
              content={{
                body: getBulkProjectActionsContent(),
                title: "Activate and Run Projects"
              }}
              testId="member-set-detail__activate-flyout"
              classNames="qes-button-secondary ms-3 pull-right"
              disabled={activateVersionButtonDisabled}
            >
              Activate and Run Projects
            </FlyoutLink>
            <QESButton qesTestId="provider-detail__activate" qesType="secondary"
              className="pull-right btn-light-gray"
              onClick={handleActivateVersionButtonClick}> Activate </QESButton>
          </div>
        </QESAlert>
        {!!availableFilteredMemberSets && availableFilteredMemberSets.length > 0 ?
          <>{filteredProvidersMultiSelect}</> : null
        }
      </div>);
  }

  const summaryInformation = (
    <div className="col-12">
      <AccordionContainer>
        <Accordion title="Members by State" testId="member-set-detail__members-by-state-accordion">
          <MemberSetCountByStateTable selectedVersion={selectedVersion} comparedVersion={selectedCompareVersion} compareMode={compareMode} />
        </Accordion>
        <Accordion title="Members by County" testId="member-set-detail__members-by-county-accordion">
          <MemberSetCountByCountyTable selectedVersion={selectedVersion} comparedVersion={selectedCompareVersion} compareMode={compareMode} />
        </Accordion>
      </AccordionContainer>
    </div>);

  const memberPreviewTable = (
    <div className="col-12">
      <MemberSetPreviewTable memberSetVersion={selectedVersion} memberCount={memberCount} />
    </div>
  );

  const memberSetInfo = (
    (memberSet ? (
      <div className="row ms-3 mt-3">
        <dl className="d-table col-auto">
          <div className="d-table-row">
            <dt className="d-table-cell fw-normal">Member Population Type: </dt>
            <dd className="d-table-cell ps-2">{memberSet.memberSetType}</dd>
          </div>
          {memberSet.resource.resourceType === ResourceType.FilteredMemberSet && sourceMemberSetInfo.length > 0 && (
            <div className="d-table-row">
              <dt className="d-table-cell fw-normal">Source Network: </dt>
              <dd className="d-table-cell ps-2">{sourceMemberSetInfo}</dd>
            </div>
          )}
          <div className="d-table-row">
            <dt className="d-table-cell fw-normal">Date Created: </dt>
            <dd className="d-table-cell ps-2">{(selectedVersion.dateCreated ? formatDate(selectedVersion.dateCreated) : null)}</dd>
          </div>
          <div className="d-table-row">
            <dt className="d-table-cell fw-normal">Date Last Updated: </dt>
            <dd className="d-table-cell ps-2">{selectedVersion.dateLastUpdated ? formatDate(selectedVersion.dateLastUpdated) : null}</dd>
          </div>
          <div className="d-table-row">
            <dt className="d-table-cell fw-normal">Date Activated: </dt>
            <dd className="d-table-cell ps-2">{selectedVersion.dateActivated ? formatDate(selectedVersion.dateActivated) : null}</dd>
          </div>
        </dl>
      </div>
    ) : null)
  );

  /*only show preview on full member populations and never on system resources*/
  const navTabs = (
    <div className="row ms-3 mt-3">
      <div className="tabs-style-2">
        <ul className="nav nav-tabs">
          <li className="nav-item"><a className={activeModule === "Summary Information" ? "nav-link active" : "nav-link"} role="tab" onClick={() => setActiveModule("Summary Information")}>Summary Information</a></li>

          {memberSet && memberSet.memberSetType === MemberSetTypeEnum.Full && !memberSet.resource.isSystemResource && !compareMode ?
            (
              <li className="nav-item"><a className={activeModule === "Preview" ? "nav-link active" : "nav-link"} role="tab" onClick={() => setActiveModule("Preview")}>Preview</a></li>
            ) : null}

        </ul>
      </div>
    </div>
  );

  const breadcrumbItemsBase = breadcrumbTemplates[BreadcrumbTemplateEnum.MemberPopulations];
  const breadcrumbCurrentPage = {
    text: !!memberSet ? memberSet.resource.name : "",
  };
  const breadcrumbReturnLink = {
    link: `/member-populations/${!!memberSet ? memberSet.memberSetId : ""}/detail`,
    text: !!memberSet ? memberSet.resource.name : "",
  };


  const canBeModified = (isQuestAdmin: boolean, isResourceAdmin: boolean, isUserOwner: boolean, sharedResource: boolean, systemResource: boolean) => {
    if (systemResource) return (isQuestAdmin || isUserOwner);
    else if (!sharedResource) return (isResourceAdmin || isUserOwner);
    return false;
  }

  const canBeActivated = (memberSet: MemberSetModel, selectedVersion: Version, filterCriteria?: FilterCriteriaModel[], newIsProcessing: boolean = isProcessing) => {
    if (newIsProcessing) {
      return false;
    }

    if (selectedVersion.memberSetVersionStatus === MemberSetVersionStatus.Processing) {
      return false;
    }

    if (!memberSet.memberSetVersions)
      return true;

    const latestVersion = memberSet.memberSetVersions.sort((a, b) => new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1)[0];

    return latestVersion.memberSetVersionId !== selectedVersion.memberSetVersionId ||
      memberSet.resource.isShared ||
      (memberSet.sourceMemberSetId ? !filterCriteria || filterCriteria.length === 0 : false);
  }

  const handleFilterSubmit = (filters: FilterCriteriaModel[]) => {
    if (processingFilters) {
      return;
    }
    setProcessingFilters(true);
    memberSetService.saveFilterCriteria(
      memberSet.memberSetId,
      selectedVersion.memberSetVersionId,
      selectedVersion.sourceMemberSetVersionId,
      memberSet.sourceMemberSetId,
      filters,
    ).then(() => {
      fetchMemberSet();
      setActivateVersionButtonDisabled(filters.length === 0);
      setError("");
      setProcessingFilters(false);
    }, () => {
      fetchMemberSet();
      setError("We encountered a problem saving your filter. Please try again.");
      setProcessingFilters(false);
    });
  }

  const handleFilterChange = (response: FilterCriteriaOnChangeResponse) => {
    setHasFilterChanged(response.hasChanges);
  }

  const buildFilterCols = async (newMemberSet: MemberSetModel = memberSet, newSelectedVersion: Version = selectedVersion) => {
    if (!newMemberSet) {
      return;
    }
    if (!newMemberSet.sourceMemberSetId) {
      return;
    }

    const convertFilterType = (filterType: string) => {
      switch (filterType.toLowerCase()) {
        case "int":
        case "decimal":
          return WafflesFilterType.number;
        case "string":
          return WafflesFilterType.string;
        case "bit":
        case "bool":
          return WafflesFilterType.boolean;
        case "datetime":
          return WafflesFilterType.dateTime;
      }
    };

    let columns: ColumnConfig = {};
    memberSetService.getMemberSetSchema(newMemberSet.sourceMemberSetId).then((apiColumns) => {
      apiColumns.forEach((cc) => {
        Object.assign(columns, {
          [cc.name]: {
            displayText: cc.name,
            filterType: convertFilterType(cc.dataType),
            testId: cc.name,
          },
        });
      });

      const usedCols = Object.entries(columns).filter((fc) => !fc[1].hidden).map((ffc) => ffc[0]);

      memberSetService.getSavedMemberSetFilters(newMemberSet.memberSetId).then((savedFilters) => {
        setActivateVersionButtonDisabled(canBeActivated(newMemberSet, newSelectedVersion, savedFilters));
        setInitialFiltersForSelectedVersion(savedFilters);
        setUsedColumns(usedCols);
      }, () => {
        setActivateVersionButtonDisabled(true);
        setInitialFiltersForSelectedVersion([]);
        setUsedColumns(usedCols);
      });
    }, () => {
      columns = {
        countySSA: {
          displayText: "County SSA",
          filterType: WafflesFilterType.string,
          testId: "county-ssa",
        },
        state: {
          displayText: "State",
          filterType: WafflesFilterType.string,
          testId: "state",
        },
        zip: {
          displayText: "Zip",
          filterType: WafflesFilterType.string,
          testId: "zip",
        },
      };
      const usedCols = Object.entries(columns).filter((fc) => !fc[1].hidden).map((ffc) => ffc[0]);

      memberSetService.getSavedMemberSetFilters(newMemberSet.memberSetId).then((savedFilters) => {
        setActivateVersionButtonDisabled(canBeActivated(newMemberSet, newSelectedVersion, savedFilters));
        setInitialFiltersForSelectedVersion(savedFilters);
        setUsedColumns(usedCols);
      }, () => {
        setActivateVersionButtonDisabled(true);
        setInitialFiltersForSelectedVersion([]);
        setUsedColumns(usedCols);
      });
    });
  }

  const fetchFiltersForSelectedVersion = async (versionId?: number): Promise<FilterCriteriaModel[]> => {
    if (!memberSet.sourceMemberSetId) {
      return [];
    }
    const selectedVersionId = versionId || selectedVersion.memberSetVersionId;
    const filters = await memberSetService.getSavedMemberSetFilters(memberSet.memberSetId, selectedVersionId);

    return filters;
  }

  const getProjectIds = async (newMemberSet: MemberSetModel = memberSet) => {
    if (!newMemberSet) return;
    const projects = await projectService.getByMemberSetId(newMemberSet.memberSetId);
    setAssociatedProjects(projects);
    setLoadingResourceIds(false);
  }

  if (memberSet) {
    const latestVersion = memberSet.memberSetVersions.sort((a, b) => new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1)[0];
    const isLatestVersion = latestVersion.memberSetVersionId === selectedVersion.memberSetVersionId;
    const isModifyable = canBeModified(userIsQuestAdmin, userIsResourceAdmin, userIsOwner, memberSet.resource.isShared, memberSet.resource.isSystemResource);

    return (
      <Page documentTitle={`${memberSet?.resource.name || "Loading"} - Member Populations`}>
        <Breadcrumbs items={[...breadcrumbItemsBase, breadcrumbCurrentPage]} />
        <PageInner>
          <PageHeader title={memberSet.resource.name}>
            <PageHeaderLeft>
              {isModifyable && !memberSet.resource.isSystemResource && (
                <>
                  <i className="fa fa-share text-muted" />
                  <Link
                    to={{
                      pathname: `/resources/share`,
                      search: "?redirectUrl=/member-populations",
                      state: {
                        breadcrumbItems: [...breadcrumbItemsBase, breadcrumbReturnLink],
                        isSingleResource: true,
                        resourceDescription: "Member Population",
                        resourceId: memberSet.memberSetId,
                        resourceTypes: [ResourceType.MemberSet],
                      },
                    }}
                  >
                    Share
                  </Link>
                </>
              )}
              {isModifyable && (
                <span className="ms-2">
                  <FlyoutLink
                    content={{
                      body: getEditMemberSetSidePanelContent(),
                      title: `Edit ${memberSet.resource.name}`,
                    }}
                    testId="member-set-detail__edit-flyout"
                  >
                    <i className="fa fa-pencil text-muted me-1" />
                    Edit
                  </FlyoutLink>
                </span>
              )}
              {isModifyable && !memberSet.resource.isSystemResource && !memberSet.sourceMemberSetId && memberSet.memberSetVersions.some((x) => x.dateActivated != null) && (
                <Link className="ms-2" to={{ pathname: "/member-populations/create-filtered", state: { sourceMemberSetId: memberSet.memberSetId } }}>
                  <i className="fa fa-filter text-muted" /> Create Filtered Member Population
                </Link>
              )}
            </PageHeaderLeft>
            <PageHeaderRight>
              <div className="btn-group pull-right">
                {isModifyable && !memberSet.resource.isArchived && (
                  <FlyoutLink
                    content={{
                      body: getMemberSetArchivePanelContent(),
                      title: `Archive ${memberSet.resource.name}`,
                    }}
                    testId="member-set-detail__archive-flyout"
                    classNames="qes-button-secondary ms-3 ps-3 pe-3"
                    size="40"
                  >
                    Archive
                  </FlyoutLink>
                )}
              </div>
            </PageHeaderRight>
          </PageHeader>
          <PageSection>
            <div className="row ms-3 mt-3 mb-1">
              {error.length > 0 && <QESAlert classNames="mb-3" type="danger" persist={true}>{error}</QESAlert>}
              <p>
                {memberSet.resource.isPrivate ? "This is a private member population (can be administered only by you)" :
                  "This member population is not private (can be administered by you and other users authorized to administer non-private member populations)"}
              </p>
            </div>
            {memberSetInfo}
            {description}
            <div className="row ms-3 mt-3">
              <div className="col-md-6">
                {membersetCompareVersionsDropdown}
              </div>
              <div className="col-md-6">
                <div className="float-end">
                  {membersetVersionsDropdown}
                </div>
              </div>
            </div>
            {selectedVersion?.memberSetVersionStatus === MemberSetVersionStatus.FailedUpload && (
              <div className="col-12 ms-3 mt-3">
                <QESAlert key="ActivationFailedAlert" type="danger" persist={true}>
                  <div className="pull-left"><strong>Error!</strong> The activation of this network version failed. Please try again later or contact support if the problem continues.</div>
                </QESAlert>
              </div>
            )}
            {showActivationButtons && activationAlert}
          </PageSection>
          {isModifyable && !memberSet.resource.isSystemResource && (
            <PageSection>
              <div className="row ms-3 mt-3">
                <div className="col-12">
                  {!hasFilterChanged && !!memberSet ?
                    (
                      <MemberSetOverallSummary
                        version={selectedVersion}
                        compareVersion={selectedCompareVersion}
                        onUpdate={(count: number) => setMemberCount(count)}
                        isHidden={isProcessing} />
                    ) : null
                  }
                  {memberSet.sourceMemberSetId && (
                    <FilterCriteria
                      fieldNames={usedColumns}
                      defaultCriteria={initialFiltersForSelectedVersion}
                      onChange={(response) => handleFilterChange(response)}
                      disabled={processingFilters || !isLatestVersion}
                      onSave={(response) => handleFilterSubmit(response)}
                    />
                  )}
                </div>
              </div>
            </PageSection>
          )}
          <PageSection>
            {(!memberSet.resource.isSystemResource || userIsQuestAdmin) && (
              navTabs
            )}
            <div className="row ms-3 mt-3">
              {hasFilterChanged && memberSet.sourceMemberSetId && (
                <QESAlert type="info" classNames="mb-3" persist={true}>Process filter criteria to update summary information.</QESAlert>
              )}
              {activeModule === "Summary Information" ? summaryInformation : activeModule === "Preview" ? memberPreviewTable : null}
            </div>
          </PageSection>
        </PageInner >
      </Page >
    );
  } else {
    return null;
  }
}

export default MemberSetDetail;
